/**
 * Creator profile (FR-ONB-003, FR-ONB-004).
 *
 * Public display name stays separate from the legal payout name. Date of
 * birth and country complete the eligibility record; neither is published.
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Button, Card, Eyebrow, PageHeader, StateBlock } from "@ui/primitives";
import { TextInput, TextArea, Select, Checkbox, FormError } from "@ui/forms";
import { fontSize } from "@design/index";
import { api, type ApiFieldErrors } from "@app/shared/api";
import { useToast } from "@app/shared/Toasts";
import { CreatorAvatar } from "@app/shared/icons";
import { FormSkeleton } from "@app/shared/Skeletons";
import { signInCopy } from "@app/shared/signIn";

interface CreatorProfile {
  display_name?: string; legal_payout_name?: string | null; country?: string | null;
  timezone?: string | null; bio?: string | null; discoverable?: boolean;
  date_of_birth?: string | null; age_confirmed_at?: string | null;
  public_fields?: string[];
  avatar_href?: string | null;
}

const COUNTRIES: readonly [string, string][] = [
  ["US", "United States"], ["GB", "United Kingdom"], ["SG", "Singapore"],
  ["LK", "Sri Lanka"], ["AU", "Australia"], ["CA", "Canada"],
  ["DE", "Germany"], ["FR", "France"], ["NL", "Netherlands"],
  ["IN", "India"], ["ID", "Indonesia"], ["MY", "Malaysia"],
  ["PH", "Philippines"], ["AE", "United Arab Emirates"], ["JP", "Japan"],
  ["KR", "South Korea"], ["BR", "Brazil"], ["MX", "Mexico"],
  ["NG", "Nigeria"], ["ZA", "South Africa"], ["IE", "Ireland"],
  ["ES", "Spain"], ["IT", "Italy"], ["SE", "Sweden"],
];

const ZONES = [
  "Asia/Singapore", "Asia/Colombo", "Asia/Dubai", "Asia/Kolkata",
  "Asia/Jakarta", "Asia/Tokyo", "Australia/Sydney",
  "Europe/London", "Europe/Berlin", "Europe/Paris",
  "America/New_York", "America/Los_Angeles", "America/Toronto", "UTC",
];

function asDateInput(value: string | null | undefined): string {
  if (!value) return "";
  const day = value.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : "";
}

export function ProfileScreen({
  emailVerified, email, signInMethod, onSaved, onSignOut,
}: {
  emailVerified: boolean;
  email?: string | undefined;
  signInMethod?: string | undefined;
  onSaved: () => void;
  onSignOut: () => void;
}) {
  const signedIn = signInCopy(signInMethod);
  return (
    <>
      <PageHeader
        eyebrow="Profile"
        title="Your creator profile"
        description="What brands see, and the private details used for payouts and eligibility. Legal name, date of birth and contact address stay off any public listing."
      />
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {signedIn.icon ? (
          <img src={signedIn.icon} alt="" width={18} height={18} />
        ) : null}
        <span style={{ fontSize: fontSize.body, color: "var(--muted)" }}>{signedIn.says}</span>
      </div>
      {emailVerified
        ? <ProfileForm onSaved={onSaved} />
        : (
          <StateBlock
            kind="forbidden"
            title="Verify your email first"
            detail={email
              ? `We sent a link to ${email}. Confirm it before you can complete this profile.`
              : "We need a confirmed address before you can complete your profile."}
          />
        )}
      <div>
        <Button size="sm" variant="ghost" onClick={onSignOut}>Sign out</Button>
      </div>
    </>
  );
}

export function ProfileForm({ onSaved }: { onSaved: () => void }) {
  const toast = useToast();
  const [loaded, setLoaded] = useState(false);
  const [p, setP] = useState<CreatorProfile>({});
  const [fields, setFields] = useState<ApiFieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api.get<{ profile: CreatorProfile | null }>("/onboarding/creator").then((r) => {
      if (r.ok && r.data.profile) {
        setP({
          ...r.data.profile,
          date_of_birth: asDateInput(r.data.profile.date_of_birth),
          country: r.data.profile.country?.toUpperCase() ?? "",
        });
      }
      setLoaded(true);
    });
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setFields({}); setFormError(null);
    const res = await api.put("/onboarding/creator", {
      displayName: p.display_name?.trim(),
      legalPayoutName: p.legal_payout_name,
      country: p.country?.toUpperCase(),
      timezone: p.timezone,
      bio: p.bio,
      dateOfBirth: asDateInput(p.date_of_birth),
      discoverable: p.discoverable ?? false,
      publicFields: p.public_fields ?? [],
    });
    setBusy(false);
    if (res.ok) {
      toast.ok("Profile saved.");
      onSaved();
      return;
    }
    if (res.fields) {
      setFields(res.fields);
      toast.error("Check the highlighted fields.");
    } else {
      setFormError(res.message);
      toast.error(res.message);
    }
  }

  if (!loaded) return <FormSkeleton />;

  const set = <K extends keyof CreatorProfile>(k: K, v: CreatorProfile[K]) =>
    setP((prev) => ({ ...prev, [k]: v }));
  const pub = new Set(p.public_fields ?? []);
  const togglePublic = (f: string, on: boolean) => {
    const next = new Set(pub);
    if (on) next.add(f); else next.delete(f);
    set("public_fields", [...next]);
  };
  const countryKnown = COUNTRIES.some(([code]) => code === (p.country ?? ""));

  return (
    <form onSubmit={submit} style={{ display: "grid", gap: 20 }} noValidate>
      {formError ? <FormError message={formError} /> : null}

      <div className="cm-profile-grid">
        <Card padding={26} style={{ display: "grid", gap: 18, borderRadius: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <ProfilePhoto
              src={p.avatar_href}
              name={p.display_name}
              onUploaded={(href) => set("avatar_href", href)}
            />
            <div style={{ display: "grid", gap: 4 }}>
              <Eyebrow>Public profile</Eyebrow>
              <strong style={{ fontSize: fontSize.h3, fontWeight: 750, letterSpacing: "-0.02em" }}>
                {p.display_name?.trim() || "Your display name"}
              </strong>
            </div>
          </div>
          <TextInput
            label="Display name"
            required
            value={p.display_name ?? ""}
            error={fields["displayName"]}
            onChange={(e) => set("display_name", e.currentTarget.value)}
            hint="Shown to brands and on any public listing."
          />
          <TextArea
            label="Short bio"
            value={p.bio ?? ""}
            error={fields["bio"]}
            onChange={(e) => set("bio", e.currentTarget.value)}
            hint="A sentence or two about the work you make."
            rows={4}
          />
          <Checkbox
            label="Let brands discover my profile"
            checked={p.discoverable ?? false}
            onChange={(v) => set("discoverable", v)}
          />
        </Card>

        <Card padding={26} style={{ display: "grid", gap: 16, borderRadius: 20 }}>
          <Eyebrow>Private - payouts and eligibility</Eyebrow>
          <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--muted)", lineHeight: 1.6 }}>
            None of this is shown publicly. The legal name must match the account that receives your payouts.
          </p>
          <TextInput
            label="Legal name for payouts"
            value={p.legal_payout_name ?? ""}
            error={fields["legalPayoutName"]}
            onChange={(e) => set("legal_payout_name", e.currentTarget.value)}
          />
          <TextInput
            label="Date of birth"
            type="date"
            required
            value={asDateInput(p.date_of_birth)}
            error={fields["dateOfBirth"]}
            onChange={(e) => set("date_of_birth", e.currentTarget.value)}
            hint="You must be at least 18 to earn on ClipMaster."
          />
          <Select
            label="Country"
            required
            value={countryKnown ? (p.country ?? "") : ""}
            error={fields["country"]}
            onChange={(e) => set("country", e.currentTarget.value)}
            hint="Payout routes depend on where the account is held."
          >
            <option value="">Select…</option>
            {COUNTRIES.map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
            {!countryKnown && p.country ? (
              <option value={p.country}>{p.country}</option>
            ) : null}
          </Select>
          <Select
            label="Timezone"
            value={p.timezone ?? ""}
            onChange={(e) => set("timezone", e.currentTarget.value)}
          >
            <option value="">Select…</option>
            {ZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </Select>
        </Card>

        <Card padding={26} style={{ display: "grid", gap: 14, borderRadius: 20 }}>
          <Eyebrow>What brands can see</Eyebrow>
          {(["bio", "country", "categories"] as const).map((f) => (
            <Checkbox
              key={f}
              label={`Publish my ${f}`}
              checked={pub.has(f)}
              onChange={(v) => togglePublic(f, v)}
            />
          ))}
          {fields["publicFields"] ? <FormError message={fields["publicFields"]} /> : null}
        </Card>
      </div>

      <div>
        <Button type="submit" variant="primary" size="lg" disabled={busy}>
          {busy ? "Saving…" : "Save profile"}
        </Button>
      </div>
    </form>
  );
}

function ProfilePhoto({
  src, name, onUploaded,
}: { src?: string | null | undefined; name?: string | undefined; onUploaded: (href: string) => void }) {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function onPick(file: File | undefined) {
    if (!file || busy) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file, file.name || "photo.jpg");
      const res = await api.postForm<{ avatarHref?: string }>("/onboarding/creator/avatar", body);
      if (!res.ok) {
        toast.error(res.message || "The photo could not be uploaded. Try again.");
        return;
      }
      if (res.data.avatarHref) onUploaded(res.data.avatarHref);
      toast.ok("Profile photo updated.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "grid", gap: 8, justifyItems: "center" }}>
      <CreatorAvatar src={src} name={name} size={72} />
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
      >
        {busy ? "Uploading…" : src ? "Change photo" : "Add photo"}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="cm-sr-only"
        tabIndex={-1}
        onChange={(e) => {
          const file = e.currentTarget.files?.[0];
          e.currentTarget.value = "";
          void onPick(file);
        }}
      />
    </div>
  );
}
